import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { backendApi } from "@/services/backend/api";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import { Lst } from "@/shared/lib/lst/lst";

const rltWorkOrdersApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltWorkOrderCreate: build.mutation<WorkOrder, CreateRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
      ],
    }),
    rltWorkOrderShow: build.query<WorkOrder, ShowRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltWorkOrder", id: result.id }] : [],
    }),
    rltWorkOrderList: build.query<Lst<WorkOrder>, ListRequest>({
      query: (request) => ({
        url: `/rlt/work-order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "RltWorkOrder", id: "LIST" }] : [],
    }),
    rltWorkOrderUpdateState: build.mutation<WorkOrder, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/work-order/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateExecutingBranch: build.mutation<
      WorkOrder,
      UpdateExecutingBranchRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/executing-branch`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateBeginDate: build.mutation<
      WorkOrder,
      UpdateBeginDateRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/begin-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateEndDate: build.mutation<WorkOrder, UpdateEndDateRequest>({
      query: (request) => ({
        url: `/rlt/work-order/end-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateTeamLeader: build.mutation<
      WorkOrder,
      UpdateTeamLeaderRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/team-leader`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateCustomerReference: build.mutation<
      WorkOrder,
      UpdateCustomerReferenceRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/customer-reference`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (result, e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
      ],
    }),
    rltWorkOrderUpdateInternalComment: build.mutation<
      WorkOrder,
      UpdateInternalCommentRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/internal-comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateServiceAddress: build.mutation<
      WorkOrder,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateServiceContact: build.mutation<
      WorkOrder,
      UpdateServiceContactRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/service-contact`,
        method: "PATCH",
        body: {
          id: request.id,
          serviceContact: request.contact,
        },
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateRepresentative: build.mutation<
      WorkOrder,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderSign: build.mutation<WorkOrder, SignRequest>({
      query: (request) => {
        const body = new FormData();
        body.append("file", request.signature);
        return {
          url: `/rlt/work-order/sign`,
          method: "PATCH",
          headers: {
            //  this is needed, otherwise a 415 error is returned
            Accept: "application/json",
          },
          params: {
            id: request.id,
            dataFormat: request.dataFormat,
            nameOfSigner: request.nameOfSigner,
          },
          body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
      ],
    }),
    rltWorkOrderDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderSetImmutable: build.mutation<WorkOrder, SetImmutableRequest>({
      query: (request) => ({
        url: `/rlt/work-order/immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderDelete: build.mutation<unknown, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
  }),
});

export const {
  useRltWorkOrderCreateMutation,
  useRltWorkOrderShowQuery,
  useRltWorkOrderListQuery,
  useRltWorkOrderUpdateStateMutation,
  useRltWorkOrderUpdateExecutingBranchMutation,
  useRltWorkOrderUpdateBeginDateMutation,
  useRltWorkOrderUpdateEndDateMutation,
  useRltWorkOrderUpdateTeamLeaderMutation,
  useRltWorkOrderUpdateCustomerReferenceMutation,
  useRltWorkOrderUpdateInternalCommentMutation,
  useRltWorkOrderUpdateServiceAddressMutation,
  useRltWorkOrderUpdateServiceContactMutation,
  useRltWorkOrderUpdateRepresentativeMutation,
  useRltWorkOrderSignMutation,
  useRltWorkOrderPreviewPDFQuery,
  useRltWorkOrderDownloadPDFMutation,
  useRltWorkOrderSetImmutableMutation,
  useRltWorkOrderDeleteMutation,
} = rltWorkOrdersApi;

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<WorkOrderOrderBy>[];
  state?: Filter<FulfillmentState>;
  executingBranch?: Filter<string>;
  beginDate?: Filter<string>;
  endDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export type WorkOrderOrderBy =
  | "beginDate"
  | "endDate"
  | "immutabilitySetAt"
  | "percentageCompleted"
  | "state"
  | "teamLeader"
  | "createdAt"
  | "updatedAt";

export interface UpdateStateRequest {
  id: string;
  state: FulfillmentState;
}

export interface UpdateExecutingBranchRequest {
  id: string;
  executingBranchId: string;
}

export interface UpdateBeginDateRequest {
  id: string;
  beginDate: string;
}

export interface UpdateEndDateRequest {
  id: string;
  endDate: string;
}

export interface UpdateTeamLeaderRequest {
  id: string;
  teamLeaderId: string;
}

export interface UpdateCustomerReferenceRequest {
  id: string;
  customerReference: string;
}

export interface UpdateInternalCommentRequest {
  id: string;
  internalComment: string;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateServiceContactRequest {
  id: string;
  // naming is contact and not serviceContact because
  // with "contact" the ContactCardWithMutation component
  // can be used. The conflict with the expected naming
  // "serviceContact" in the backend is resolved in the
  // mutation definition above.
  contact: Contact;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: WorkOrderDocument;
}

export enum WorkOrderDocument {
  All = "all",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  WorkOrder = "work_order",
  LastMinuteRiskAnalysis = "lmra",

  Documentation = "documentation",
}

export interface SetImmutableRequest {
  id: string;
  userId: string;
  reason: string;
}

export interface DeleteRequest {
  id: string;
}
